import { ChevronDown, LogOut, Mail, Menu, X, type LucideIcon } from 'lucide-react'
import { useEffect, useState } from 'react'
import { Link, NavLink, Outlet, useNavigate, useLocation } from 'react-router-dom'
import { api } from '../api/client'
import type { SiteConfig } from '../api/types'
import { useI18n } from '../i18n'
import { cn } from '../lib/cn'
import { LanguageSelect } from './LanguageSelect'

export type NavItem = { to: string; icon: LucideIcon; label: string }
export type NavGroup = { label: string; icon: LucideIcon; items: NavItem[] }
export type NavEntry = NavItem | NavGroup

function isGroup(entry: NavEntry): entry is NavGroup {
  return 'items' in entry
}

export function AppShell({ items, audience }: { items: NavEntry[]; audience: 'admin' | 'user' }) {
  const { t } = useI18n()
  const navigate = useNavigate()
  const location = useLocation()
  const [mobileOpen, setMobileOpen] = useState(false)
  const [siteConfig, setSiteConfig] = useState<SiteConfig | null>(null)
  const [openGroups, setOpenGroups] = useState<Set<string>>(() => {
    const set = new Set<string>()
    for (const entry of items) {
      if (isGroup(entry) && entry.items.some(i => location.pathname.startsWith(i.to))) {
        set.add(entry.label)
      }
    }
    return set
  })

  useEffect(() => {
    api.get<SiteConfig>('/site-config').then(setSiteConfig).catch(() => {})
  }, [])

  function toggleGroup(label: string) {
    setOpenGroups(prev => {
      const next = new Set(prev)
      next.has(label) ? next.delete(label) : next.add(label)
      return next
    })
  }

  const logoElement = siteConfig?.logoUrl
    ? <img src={siteConfig.logoUrl} alt="" className="h-8 w-8 rounded-lg object-cover shadow-sm" />
    : <span className="flex items-center justify-center rounded-lg bg-primary p-2 text-primary-foreground shadow-sm"><Mail size={18}/></span>

  const logoElementSmall = siteConfig?.logoUrl
    ? <img src={siteConfig.logoUrl} alt="" className="h-7 w-7 rounded-lg object-cover" />
    : <span className="flex items-center justify-center rounded-lg bg-primary p-1.5 text-primary-foreground"><Mail size={16}/></span>

  const renderNavItem = (item: NavItem) => (
    <NavLink key={item.to} to={item.to} end={item.to === `/${audience}`} onClick={() => setMobileOpen(false)} className={({ isActive }) => cn('nav-link', isActive && 'nav-link-active')}>
      <item.icon size={17}/>{t(item.label)}
    </NavLink>
  )

  const renderNav = () => (
    <nav className="flex flex-col gap-0.5 px-3 py-2">
      {items.map((entry) => {
        if (!isGroup(entry)) return renderNavItem(entry)
        const isOpen = openGroups.has(entry.label)
        const isActiveGroup = entry.items.some(i => location.pathname.startsWith(i.to))
        return (
          <div key={entry.label}>
            <button onClick={() => toggleGroup(entry.label)} className={cn('nav-link w-full justify-between', isActiveGroup && 'text-foreground font-medium')}>
              <span className="flex items-center gap-2.5"><entry.icon size={17}/>{t(entry.label)}</span>
              <ChevronDown size={14} className={cn('transition-transform', isOpen && 'rotate-180')} />
            </button>
            {isOpen && (
              <div className="ml-4 mt-0.5 flex flex-col gap-0.5 border-l pl-2">
                {entry.items.map(renderNavItem)}
              </div>
            )}
          </div>
        )
      })}
    </nav>
  )

  const sidebar = (
    <>
      <Link to="/" className="flex h-16 items-center gap-3 px-5 hover:opacity-80 transition-opacity">
        {logoElement}
        <span className="font-semibold tracking-tight">{t('common.appName')}</span>
      </Link>
      {renderNav()}
      <div className="mt-auto border-t p-3">
        <div className="mb-2 flex items-center justify-between px-3">
          <span className="text-xs text-muted-foreground">{audience === 'admin' ? t('auth.adminTitle') : t('user.mail')}</span>
          <LanguageSelect />
        </div>
        <button className="nav-link w-full" onClick={async () => { try { await api.logout() } finally { navigate(`/${audience}/login`) } }}>
          <LogOut size={17}/>{t('common.signOut')}
        </button>
        <p className="mt-2 px-3 text-[10px] text-muted-foreground/60">v0.2.0 · {__BUILD_TIME__.slice(0, 16).replace('T', ' ')}</p>
      </div>
    </>
  )

  return (
    <div className="min-h-screen bg-background lg:grid lg:grid-cols-[16rem_1fr]">
      {/* Desktop sidebar */}
      <aside className="hidden lg:fixed lg:inset-y-0 lg:flex lg:w-64 lg:flex-col lg:border-r lg:bg-card/80 lg:backdrop-blur-sm">
        {sidebar}
      </aside>

      {/* Mobile header */}
      <header className="sticky top-0 z-40 flex h-14 items-center justify-between border-b bg-card/90 px-4 backdrop-blur-md lg:hidden">
        <Link to="/" className="flex items-center gap-2 hover:opacity-80 transition-opacity">
          {logoElementSmall}
          <span className="text-sm font-semibold">{t('common.appName')}</span>
        </Link>
        <button onClick={() => setMobileOpen(!mobileOpen)} className="rounded-lg p-2 transition hover:bg-muted" aria-label="Menu">
          {mobileOpen ? <X size={20}/> : <Menu size={20}/>}
        </button>
      </header>

      {/* Mobile drawer */}
      {mobileOpen && (
        <div className="fixed inset-0 z-50 lg:hidden">
          <div className="absolute inset-0 bg-black/40 animate-in fade-in" onClick={() => setMobileOpen(false)} />
          <aside className="absolute inset-y-0 left-0 flex w-72 flex-col bg-card shadow-xl animate-in slide-in-from-top">
            {sidebar}
          </aside>
        </div>
      )}

      <main className="min-w-0 px-4 py-5 sm:px-6 sm:py-7 lg:col-start-2 lg:px-10 lg:py-8">
        <Outlet />
      </main>
    </div>
  )
}
